import { useState } from 'react';

import PropTypes from 'prop-types';

import { useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import CircularProgress from '@zenbusiness/zui/CircularProgress';
import FormControl from '@zenbusiness/zui/FormControl';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';

import { useChangeTypeReasons } from '../../../hooks/useChangeTypeReasons';
import {
  StyledReasonSection
} from '../SubscriptionCancelPage.styles';

const MAX_DESCRIPTION_LENGTH = 255;

export function SubscriptionCancelReason({
  changeReason,
  changeDescription,
  onChangeReason,
  onChangeReasonDisplay,
  onChangeDescription
}) {
  const { formatMessage } = useIntl();
  const [descriptionError, setDescriptionError] = useState(false);

  const { data, loading } = useChangeTypeReasons('cancel_renewal');

  const reasons = (data?.cancelRenewal || []).filter((r) => r.show);

  const handleDescriptionChange = (e) => {
    onChangeDescription(e.target.value);
    if (descriptionError && e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescriptionError(false);
    }
  };

  const setDescriptionErrorIfOverMax = (value) => {
    if (value.length > MAX_DESCRIPTION_LENGTH) {
      setDescriptionError(true);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" padding={6}>
        <CircularProgress
          aria-label={formatMessage({ id: 'dashboard.subscriptionCancel.reason.loading.ariaLabel' })}
        />
      </Box>
    );
  }

  return (
    <StyledReasonSection>
      <Box display="flex" flexDirection="column" gap={1.5}>
        <Text size="2xl" weight="bold" family="secondary">
          {formatMessage({ id: 'dashboard.subscriptionCancel.reason.heading' })}
        </Text>
        <Text size="md" color="text.secondary">
          {formatMessage({ id: 'dashboard.subscriptionCancel.reason.subheading' })}
        </Text>
      </Box>

      <FormControl>
        <Text size="lg" weight="bold" sx={{ mb: 1 }}>
          <span>
            {formatMessage({ id: 'dashboard.subscriptionCancel.reason.label' })}
            {' '}
          </span>
          <span style={{ fontWeight: 400 }}>
            {formatMessage({ id: 'dashboard.subscriptionCancel.reason.required' })}
          </span>
        </Text>
        <RadioGroup
          value={changeReason}
          onChange={(e) => {
            const { value } = e.target;
            onChangeReason(value);
            const selected = reasons.find((r) => r.value === value);
            onChangeReasonDisplay?.(selected?.display ?? '');
          }}
        >
          {reasons.map((reason) => (
            <FormControlLabel
              key={reason.value}
              value={reason.value}
              control={<Radio />}
              label={reason.display}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Text size="lg" weight="bold">
            {formatMessage({ id: 'dashboard.subscriptionCancel.reason.feedbackTitle' })}
          </Text>
          <Text size="sm" color="text.tertiary">
            {formatMessage({ id: 'dashboard.subscriptionCancel.reason.feedbackDescription' })}
          </Text>
        </Box>
        <TextField
          multiline
          minRows={6}
          placeholder={formatMessage({ id: 'dashboard.subscriptionCancel.reason.feedbackPlaceholder' })}
          value={changeDescription}
          onChange={handleDescriptionChange}
          onBlur={(e) => setDescriptionErrorIfOverMax(e.target.value)}
          error={descriptionError}
          helperText={`${changeDescription.length}/${MAX_DESCRIPTION_LENGTH}`}
        />
      </Box>
    </StyledReasonSection>
  );
}

SubscriptionCancelReason.propTypes = {
  changeReason: PropTypes.string.isRequired,
  changeDescription: PropTypes.string.isRequired,
  onChangeReason: PropTypes.func.isRequired,
  onChangeReasonDisplay: PropTypes.func,
  onChangeDescription: PropTypes.func.isRequired
};

SubscriptionCancelReason.defaultProps = {
  onChangeReasonDisplay: null
};
